import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import {
  ActionIcon,
  AvailableIcons,
  Button,
  Typography,
  TypographyType,
  TypographyVariant,
} from 'src/components-dummy';
import {
  ExperimentMetric,
  ExperimentStatus,
  WizardMetricBaseline,
  experimentService,
} from 'src/services';
import { editExperimentContainerActionMethods } from 'src/containers/EditExperimentContainer/Actions';
import { WizardStepIndicator } from './WizardStepIndicator';
import { GoalStep } from './GoalStep';
import { GoalStepControls } from './GoalStep/types';
import { TIER_A_METRICS } from './GoalStep/constants';
import { WhatToTestStep } from './WhatToTestStep';
import { WhatToTestStepControls } from './WhatToTestStep/types';
import { VariantsStep } from './VariantsStep';
import { VariantsStepControls } from './VariantsStep/types';
import { TrafficStep } from './TrafficStep';
import { TrafficStepControls } from './TrafficStep/types';
import { ReviewStep } from './ReviewStep';
import { ReviewStepControls } from './ReviewStep/types';
import { ExperimentWizardProps, StepControls } from './types';
import { useProvisioningRetry } from './useProvisioningRetry';
import './ExperimentWizard.scss';

type SubmittableStep = 1 | 2 | 3 | 4;

export const ExperimentWizard = ({
  shopId,
  experiment,
  dispatch,
  permittedRouteMap,
  onClose,
  hasRunningExperiment,
  runningExperimentId,
  onLaunched,
}: ExperimentWizardProps): JSX.Element => {
  const history = useHistory();
  const { step } = useParams<{ step: string }>();
  const activeStep = Number(step) || 1;
  const [stepControlsByStep, setStepControlsByStep] = useState<
    Partial<Record<SubmittableStep, StepControls>>
  >({});
  const [reviewStepControls, setReviewStepControls] = useState<ReviewStepControls | undefined>();
  const [baselines, setBaselines] = useState<WizardMetricBaseline[]>([]);
  const [isLoadingBaselines, setIsLoadingBaselines] = useState(true);
  const [goalMetric, setGoalMetric] = useState<ExperimentMetric | undefined>(experiment?.metric);

  // Catches up goalMetric when `experiment` finishes loading (or changes) after mount,
  useEffect(() => {
    if (experiment?.metric !== undefined) {
      setGoalMetric(experiment.metric);
    }
  }, [experiment?.metric]);

  useLayoutEffect(() => {
    setStepControlsByStep({});
    setReviewStepControls(undefined);
  }, [activeStep]);

  const setStepControls = useCallback((stepNumber: SubmittableStep, controls: StepControls) => {
    setStepControlsByStep(prev => ({ ...prev, [stepNumber]: controls }));
  }, []);

  const handleGoalStepControlsChange = useCallback(
    (controls: GoalStepControls): void => {
      setGoalMetric(controls.selectedMetric);
      setStepControls(1, controls);
    },
    [setStepControls]
  );

  const handleWhatToTestStepControlsChange = useCallback(
    (controls: WhatToTestStepControls): void => setStepControls(2, controls),
    [setStepControls]
  );

  const handleVariantsStepControlsChange = useCallback(
    (controls: VariantsStepControls): void => setStepControls(3, controls),
    [setStepControls]
  );

  const handleTrafficStepControlsChange = useCallback(
    (controls: TrafficStepControls): void => setStepControls(4, controls),
    [setStepControls]
  );

  useEffect(() => {
    let cancelled = false;

    setIsLoadingBaselines(true);
    experimentService
      .getWizardMetricBaselines({ shopId, metrics: TIER_A_METRICS.map(({ metric }) => metric) })
      .then(({ metrics }) => {
        if (!cancelled) {
          setBaselines(metrics);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingBaselines(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [shopId]);

  const goToStep = useCallback(
    (nextStep: number): void => {
      const wizardRoute = permittedRouteMap.experimentWizard?.path;

      if (wizardRoute && experiment) {
        history.push(
          generatePath(wizardRoute, { shopId, experimentId: experiment.id, step: nextStep })
        );
      }
    },
    [history, permittedRouteMap, shopId, experiment]
  );

  const refreshExperiment = useCallback(async (): Promise<void> => {
    if (!experiment) return;
    await dispatch(
      editExperimentContainerActionMethods.getExperiment({
        shopId,
        id: experiment.id,
        slim: true,
      })
    );
  }, [dispatch, shopId, experiment]);

  useEffect(() => {
    if (experiment?.status !== ExperimentStatus.Provisioning || experiment?.provisioningError) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      refreshExperiment();
    }, 2000);

    return () => clearInterval(intervalId);
  }, [experiment?.status, experiment?.provisioningError, refreshExperiment]);

  const { isRetrying, retryError, onRetryProvisioning } = useProvisioningRetry({
    shopId,
    experimentId: experiment?.id,
    onRetried: refreshExperiment,
  });

  const isLocked = experiment?.status === ExperimentStatus.Provisioning;

  const onBack = (): void => {
    if (activeStep <= 1) {
      onClose();
    } else {
      goToStep(activeStep - 1);
    }
  };

  const advanceToStep = useCallback(
    async (targetStep: number): Promise<void> => {
      if (targetStep > activeStep && isLocked) return;

      const activeStepControls = stepControlsByStep[activeStep as SubmittableStep];

      if (targetStep > activeStep && activeStepControls) {
        if (!activeStepControls.canContinue) return;
        if (!(await activeStepControls.onSubmit())) return;
        await refreshExperiment();
      }

      goToStep(targetStep);
    },
    [activeStep, isLocked, stepControlsByStep, goToStep, refreshExperiment]
  );

  const onContinue = (): Promise<void> => advanceToStep(activeStep + 1);
  const onStepIndicatorClick = (targetStep: number): Promise<void> => advanceToStep(targetStep);

  const activeStepControls = stepControlsByStep[activeStep as SubmittableStep];

  return (
    <div className='syte-experiment-wizard'>
      {hasRunningExperiment && (
        <div className='syte-experiment-wizard-status'>
          <span className='syte-experiment-wizard-status-dot' />
          <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
            Experiment running
          </Typography>
        </div>
      )}

      <div className='syte-experiment-wizard-nav'>
        <div className='syte-experiment-wizard-nav-left'>
          <ActionIcon iconName={AvailableIcons.Close} onClick={onClose} />
          <div className='syte-experiment-wizard-nav-left-title'>
            <Typography
              type={TypographyType.Body}
              variant={TypographyVariant.SmallBold}
              className='syte-experiment-wizard-nav-left-title-eyebrow'
            >
              New Experiment
            </Typography>
            <Typography type={TypographyType.Heading} variant={TypographyVariant.SmallBold}>
              {experiment?.name || 'New Experiment'}
            </Typography>
          </div>
        </div>

        <div className='syte-experiment-wizard-nav-center'>
          <WizardStepIndicator activeStep={activeStep} onStepClick={onStepIndicatorClick} />
        </div>

        <div className='syte-experiment-wizard-nav-right'>
          <Button variant='secondary' onClick={onBack}>
            Back
          </Button>
          {activeStep === 5 ? (
            <Button
              variant='primary'
              disabled={
                isLocked ||
                !reviewStepControls ||
                !reviewStepControls.canLaunch ||
                reviewStepControls.isLaunching
              }
              onClick={reviewStepControls?.onLaunchClick}
            >
              Launch
            </Button>
          ) : (
            <Button
              variant='primary'
              disabled={
                isLocked ||
                !activeStepControls ||
                !activeStepControls.canContinue ||
                activeStepControls.isSubmitting
              }
              onClick={onContinue}
            >
              Continue
            </Button>
          )}
        </div>
      </div>

      {isLocked && (
        <div className='syte-experiment-wizard-provisioning-banner'>
          {experiment?.provisioningError ? (
            <>
              <Typography type={TypographyType.Body} variant={TypographyVariant.MediumRegular}>
                Something went wrong setting up your variants: {experiment.provisioningError}. You
                can retry, or delete this experiment and start again.
              </Typography>
              <Button
                variant='secondary'
                onClick={onRetryProvisioning}
                disabled={isRetrying}
                loading={isRetrying}
              >
                Retry
              </Button>
              {retryError && (
                <Typography
                  type={TypographyType.Body}
                  variant={TypographyVariant.SmallRegular}
                  className='syte-experiment-wizard-provisioning-banner-retry-error'
                >
                  {retryError}
                </Typography>
              )}
            </>
          ) : (
            <Typography type={TypographyType.Body} variant={TypographyVariant.MediumRegular}>
              Still setting up your variants, this will only take a moment...
            </Typography>
          )}
        </div>
      )}

      <div className='syte-experiment-wizard-body'>
        {activeStep === 1 && (
          <GoalStep
            shopId={shopId}
            experiment={experiment}
            baselines={baselines}
            isLoadingBaselines={isLoadingBaselines}
            onStepControlsChange={handleGoalStepControlsChange}
          />
        )}
        {activeStep === 2 && (
          <WhatToTestStep
            shopId={shopId}
            experiment={experiment}
            goalMetric={goalMetric}
            dataFieldsRoute={permittedRouteMap.dataFields?.path}
            onStepControlsChange={handleWhatToTestStepControlsChange}
          />
        )}
        {activeStep === 3 && (
          <VariantsStep
            shopId={shopId}
            experiment={experiment}
            onStepControlsChange={handleVariantsStepControlsChange}
          />
        )}
        {activeStep === 4 && (
          <TrafficStep
            shopId={shopId}
            experiment={experiment}
            goalMetric={goalMetric}
            onStepControlsChange={handleTrafficStepControlsChange}
          />
        )}
        {activeStep === 5 && (
          <ReviewStep
            shopId={shopId}
            experiment={experiment}
            goalMetric={goalMetric}
            dispatch={dispatch}
            runningExperimentId={runningExperimentId}
            onLaunched={onLaunched}
            onClose={onClose}
            onStepControlsChange={setReviewStepControls}
          />
        )}
        {activeStep > 5 && (
          <Typography type={TypographyType.Body} variant={TypographyVariant.MediumRegular}>
            This step is coming soon.
          </Typography>
        )}
      </div>
    </div>
  );
};
